import clsx from 'clsx';
import {
  Button,
  ConfirmationModalContent,
  DatePicker,
  Dropdown,
  ErrorContainer,
  LabelComponent,
  LoadingSpinner,
  ModalPopup,
  Stack,
  Text,
  Tooltip
} from 'components';
import DateAndTimePopoverContainer, {
  isDateTodayOrPast
} from 'components/datePicker/dateTimepopover/dateAndTimePopover';
import AudioAssist from 'components/ocrAudio/audioAssist';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useFormik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import debounce from 'lodash.debounce';
import {
  getLsUserRoleObject,
  removeCurrentNote,
  setCurrentNoteID
} from 'modules/networkTools/localStorageTokens';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  deleteNoteInput,
  getClientNote,
  getClientNotes,
  getUsersImageNotes,
  getZCodes,
  postCreateNote,
  putUpdateNote
} from 'store/clientProfile/clientProfileActions';
import {
  clearZCodes,
  ClientProfileContentItem,
  removeNote
} from 'store/clientProfile/clientProfileSlice';
import { clientReachedData, noteTypeData, typesOfSupportData } from 'store/clients/clientsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getTodos as getMyTodos } from 'store/myTodos/myTodosActions';
import { getTodos as getRoadmapTodos } from 'store/roadmaps/roadmapsActions';
import { notifyUserSuccess } from 'utils/notifications';
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';

import { JoyTextArea } from '../../joyTextArea';
import { LastSaved } from '../noteView/addendumForm/lastSaved';
import style from './noteEdit.module.scss';
import Transcript from './transcript/transcript';
import ZCodesSelect from './zCodesSelect/zCodesSelect';
import { AudioPlayer } from './audioPlayer';
dayjs.extend(isSameOrAfter);

export type Props = {
  note?: ClientProfileContentItem;
  clientId: string;
  isEditMode?: boolean;
  inputOpen?: boolean;
  noteId?: string;
  context: 'popup' | 'noteSection';
};

const isValidDateOrTimeOrNull = (value: any, type: 'date' | 'time') => {
  const parsed = type === 'date' ? dayjs(value) : dayjs(value, ['hh:mm A', 'HH:mm:ss'], true);

  const isValid = parsed.isValid();

  if (isValid) {
    if (type === 'date' && !isDateTodayOrPast(value)) return null;
    return value;
  } else {
    return null;
  }
};

export function useIsFirstRender(): boolean {
  const isFirst = useRef(true);

  if (isFirst.current) {
    isFirst.current = false;

    return true;
  }

  return isFirst.current;
}

export const timeSpentData = [
  { id: '0', name: '0min' },
  { id: '5', name: '5min' },
  { id: '10', name: '10min' },
  { id: '15', name: '15min' },
  { id: '30', name: '30min' },
  { id: '45 ', name: '45min' },
  { id: '60', name: '1hr' },
  { id: '75', name: '1hr 15min' },
  { id: '90', name: '1hr 30min' },
  { id: '105', name: '1hr 45min' },
  { id: '120', name: '2hr' },
  { id: '150', name: '2hr 30min' },
  { id: '180 ', name: '3hr' },
  { id: '210', name: '3hr 30min' },
  { id: '240', name: '4hr' },
  { id: '270 ', name: '>4hr' }
];

const NextContact = ({
  formik,
  disabled
}: {
  formik: {
    setFieldValue: ReturnType<typeof useFormik>['setFieldValue'];
    setFieldTouched: ReturnType<typeof useFormik>['setFieldTouched'];
    values: {
      nextContact: string;
      dateOfEncounter: string;
    };
    touched: {
      nextContact: boolean;
    };
    errors: {
      nextContact: string;
    };
  };
  disabled: boolean;
}) => {
  return (
    <>
      <LabelComponent text="Next Contact" />
      <DatePicker
        withGradient
        onChange={(date: string) => {
          formik.setFieldValue('nextContact', date).then(() => {
            formik.setFieldTouched('nextContact', true);
          });
        }}
        onBlur={() => formik.setFieldTouched('nextContact', true)}
        value={formik.values.nextContact}
        placeholder="MM/DD/YYYY"
        minDate={
          formik.values.dateOfEncounter != null && formik.values.dateOfEncounter !== ''
            ? dayjs(formik.values.dateOfEncounter).add(1, 'day')
            : undefined
        }
        disablePast={false}
        disabled={disabled}
      />
      <ErrorContainer
        visible={formik.touched?.nextContact && (formik.errors?.nextContact as string)}
        errors={formik.errors}
        name="nextContact"
        autoHeight
      />
    </>
  );
};

const TimeSpent = ({
  formik,
  disabled
}: {
  formik: {
    setFieldValue: ReturnType<typeof useFormik>['setFieldValue'];
    setFieldTouched: ReturnType<typeof useFormik>['setFieldTouched'];
    handleChange: ReturnType<typeof useFormik>['handleChange'];
    handleBlur: ReturnType<typeof useFormik>['handleBlur'];
    values: {
      timeSpent: number | null;
    };
    touched: {
      timeSpent: boolean;
    };
    errors: {
      timeSpent: string;
    };
  };
  disabled: boolean;
}) => {
  return (
    <>
      <LabelComponent mandatory text="Time spent" />
      <Dropdown
        smallDropdown
        label="Select"
        data={timeSpentData}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.timeSpent?.toString() ?? ''}
        withGradient
        name="timeSpent"
        disabled={disabled}
      />
      <ErrorContainer
        visible={formik.touched.timeSpent && formik.errors.timeSpent}
        errors={formik.errors}
        name="timeSpent"
      />
    </>
  );
};

const TypeOfSupport = ({
  formik,
  disabled,
  typesOfSupport
}: {
  formik: {
    setFieldValue: ReturnType<typeof useFormik>['setFieldValue'];
    setFieldTouched: ReturnType<typeof useFormik>['setFieldTouched'];
    handleChange: ReturnType<typeof useFormik>['handleChange'];
    handleBlur: ReturnType<typeof useFormik>['handleBlur'];
    values: {
      typeOfSupport: string[] | string;
    };
    touched: {
      typeOfSupport: boolean;
    };
    errors: {
      typeOfSupport: string | string[];
    };
  };
  disabled: boolean;
  typesOfSupport: any[];
}) => {
  return (
    <>
      <Stack display="flex" flexDirection="row" alignItems="center" gap="4px">
        <LabelComponent mandatory text="Type of Support" />
        <Tooltip
          placement="right"
          toolTipText={
            <Stack
              width="333px"
              display="flex"
              gap="8px"
              padding="4px"
              className={style.typeOfSupportInfo}
            >
              <p>Here's what the different kinds of support mean:</p>
              <ul>
                <li>
                  <span>Social support</span> is being there for someone, e.g. going for a walk or
                  making a care package
                </li>
                <li>
                  <span>Coaching for health habits</span> is working to help someone achieve their
                  well-being goals, e.g. working out with them at the YMCA
                </li>
                <li>
                  <span>Navigation for healthcare and Navigation for mental healthcare</span> are
                  helping someone get what they need from the healthcare system, e.g. going with
                  them to an appointment
                </li>
                <li>
                  <span>Advocacy for empowerment</span> is speaking up with and on behalf of
                  someone, e.g. organizing a community action group
                </li>
                <li>
                  <span>Problem solving</span> is taking action to address specific challenges.
                  Remember to take action, don't just hand out a referral!
                </li>
              </ul>
            </Stack>
          }
        >
          <span className={clsx('material-icons', style.infoIcon)}>info</span>
        </Tooltip>
      </Stack>
      <Dropdown
        smallDropdown
        withGradient
        label="Select"
        data={typesOfSupport}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        name="typeOfSupport"
        id="typeOfSupport"
        value={formik.values.typeOfSupport}
        categoryList={[{ id: 'PROBLEM_SOLVING', label: 'Problem solving' }]}
        categorySelectable
        multiSelect
        disabled={disabled}
      />
      <ErrorContainer
        autoHeight
        name="typeOfSupport"
        errors={formik.errors}
        visible={formik.touched.typeOfSupport && Boolean(formik.errors.typeOfSupport)}
      />
    </>
  );
};

const ZCodesField = ({
  formik,
  disabled
}: {
  formik: {
    setFieldValue: ReturnType<typeof useFormik>['setFieldValue'];
    setFieldTouched: ReturnType<typeof useFormik>['setFieldTouched'];
    handleChange: ReturnType<typeof useFormik>['handleChange'];
    handleBlur: ReturnType<typeof useFormik>['handleBlur'];
    values: {
      zCodes: { id: number; name: string }[];
    };
  };
  disabled: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { clientId = '' } = useParams();
  const { values, setFieldValue } = formik;
  const [searchInputValue, setSearchInputValue] = useState('');
  const favoriteZCodes = useAppSelector((state) => state.clientProfile.favoriteZCodes.data);
  const zCodes = useAppSelector((state) => state.clientProfile.zCodes.data);
  const pendingGetZCodesRef = useRef<{ abort: () => void } | null>(null);

  const mappedFavoriteZCodes = favoriteZCodes.map((z) => ({
    id: z.id,
    name: `${z.code} - ${z.description}`
  }));

  const mappedZCodes = zCodes.map((z) => ({ id: z.id, name: `${z.code} - ${z.description}` }));

  const data = searchInputValue ? mappedZCodes : mappedFavoriteZCodes;

  const debouncedSearch = useCallback(
    debounce(
      (
        query: string,
        _pendingGetZCodesRef: React.MutableRefObject<{ abort: () => void } | null>
      ) => {
        if (query) {
          const promise = dispatch(getZCodes({ query }));
          _pendingGetZCodesRef.current = promise;
        }
      },
      200
    ),
    [clientId, dispatch]
  );

  const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInputValue(value);
    pendingGetZCodesRef.current?.abort();
    if (value === '') {
      dispatch(clearZCodes());
    } else {
      debouncedSearch(value, pendingGetZCodesRef);
    }
  };

  return (
    <Stack width="100%" mb="16px">
      <Stack display="flex" flexDirection="row" alignItems="center" gap="4px">
        <LabelComponent text="SDOH Z codes" />
        <Tooltip
          placement="right"
          toolTipText={
            <Stack
              width="333px"
              display="flex"
              gap="8px"
              padding="4px"
              className={style.typeOfSupportInfo}
            >
              <p>
                These codes help track the social and environmental factors that impact a client's
                health. Please select the codes that were most relevant to this encounter.
              </p>
            </Stack>
          }
        >
          <span className={clsx('material-icons', style.infoIcon)}>info</span>
        </Tooltip>
      </Stack>
      <ZCodesSelect
        showTitle={searchInputValue !== '' ? false : true}
        data={data.map((z) => ({ id: z.id, name: z.name, info: z.name }))}
        value={values.zCodes.map((z) => ({
          id: z.id.toString(),
          name: z.name
        }))}
        onClose={() => {
          setSearchInputValue('');
          dispatch(clearZCodes());
        }}
        searchInputValue={searchInputValue}
        onSearchInputChange={(e) => onSearchInputChange(e)}
        onChange={(e) => {
          if (Array.isArray(e.target.value)) {
            const removedFromCurrentValues = values.zCodes.filter(
              (zCode) => !e.target.value.includes(zCode.id.toString())
            );

            if (removedFromCurrentValues.length > 0) {
              setFieldValue(
                'zCodes',
                values.zCodes.filter(
                  (z) => !removedFromCurrentValues.some((zCode) => zCode.id === z.id)
                )
              );
              return;
            }

            const notInCurrentValues = e.target.value.filter(
              (zCode) => !values.zCodes.find((z) => z.id.toString() === zCode)
            );

            const found = data.find((zCode) => zCode.id.toString() === notInCurrentValues[0]);

            if (found) {
              setFieldValue('zCodes', [...values.zCodes, found]);
            }
          }
        }}
        disabled={disabled}
        withGradient
        label="Select"
      />
    </Stack>
  );
};

const validationSchema = Yup.object({
  note: Yup.string().required('Please enter a note to save.').nullable(),
  clientReached: Yup.string().required('Client reached is required.').nullable(),
  noteType: Yup.string().required('Note type is required.').nullable(),
  dateOfEncounter: Yup.string()
    .nullable()
    .required('Date of Encounter is required.')
    .test(
      'dateOfEncounter',
      "Date of Encounter can't be in the future.",
      async (fieldVal, values): Promise<boolean | Yup.ValidationError | any> => {
        if (!fieldVal) return false;

        // Validate the date format
        const parsedDate = dayjs(fieldVal, 'MM/DD/YYYY', true);
        if (!parsedDate.isValid()) {
          return false;
        }

        // Check if date is today or in the past
        if (parsedDate.isAfter(dayjs(), 'day')) {
          return false;
        }

        return true; // If all checks pass, validation succeeds
      }
    ),

  startTimeOfEncounter: Yup.string()
    .nullable()
    .required('Time is required.')
    .test(
      'startTimeOfEncounter',
      'Time is required.',
      async (fieldVal, values): Promise<boolean | Yup.ValidationError | any> => {
        if (!fieldVal || fieldVal === 'Invalid Date') return false;

        return true;
      }
    ),
  nextContact: Yup.string()
    .nullable()
    .test(
      'nextContact',
      'Date of Next Contact must be after Date of Encounter.',
      async (fieldVal, values): Promise<boolean | Yup.ValidationError | any> => {
        const formikValues = values.parent;

        if (fieldVal == null || fieldVal === '') {
          return true;
        }

        if (formikValues.dateOfEncounter == null || formikValues.dateOfEncounter === '') {
          return true;
        }
        //check if end time after start time
        let isStartTimeBeforeEndTime = true;
        const startTime = dayjs(`${formikValues.dateOfEncounter}`);

        const endTime = dayjs(`${formikValues.nextContact}`);

        isStartTimeBeforeEndTime = startTime.isBefore(endTime);

        return isStartTimeBeforeEndTime;
      }
    ),

  typeOfSupport: Yup.array()
    .of(Yup.string())
    .min(1, 'Type of support is required.')
    .required('Type of support is required.'),
  timeSpent: Yup.number().nullable().required('Time Spent is required.')
});

const sitesSchema = Yup.object({
  siteId: Yup.number().nullable().required('Site is required')
});

const validationSchemaWithSites = validationSchema.concat(sitesSchema);

const AiScribeIcon = () => {
  return (
    <svg width="20" height="22" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M17.1065 7.00002L17.8965 5.25002L19.6465 4.46002C20.0365 4.28002 20.0365 3.73002 19.6465 3.55002L17.8965 2.76002L17.1065 1.00002C16.9265 0.61002 16.3765 0.61002 16.1965 1.00002L15.4065 2.75002L13.6465 3.54002C13.2565 3.72002 13.2565 4.27002 13.6465 4.45002L15.3965 5.24002L16.1865 7.00002C16.3665 7.39002 16.9265 7.39002 17.1065 7.00002ZM9.14652 8.50002L7.55652 5.00002C7.20652 4.22002 6.08652 4.22002 5.73652 5.00002L4.14652 8.50002L0.646523 10.09C-0.133477 10.45 -0.133477 11.56 0.646523 11.91L4.14652 13.5L5.73652 17C6.09652 17.78 7.20652 17.78 7.55652 17L9.14652 13.5L12.6465 11.91C13.4265 11.55 13.4265 10.44 12.6465 10.09L9.14652 8.50002ZM16.1865 15L15.3965 16.75L13.6465 17.54C13.2565 17.72 13.2565 18.27 13.6465 18.45L15.3965 19.24L16.1865 21C16.3665 21.39 16.9165 21.39 17.0965 21L17.8865 19.25L19.6465 18.46C20.0365 18.28 20.0365 17.73 19.6465 17.55L17.8965 16.76L17.1065 15C16.9265 14.61 16.3665 14.61 16.1865 15Z"
        fill="#188AA1"
      />
    </svg>
  );
};

const NoteEdit = ({ note, clientId, inputOpen, noteId, context }: Props) => {
  const [isAutosaving, setIsAutosaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [formDisabled, setFormDisabled] = useState(context === 'popup');
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const currentOrganizationID = getLsUserRoleObject()?.currentOrganization?.id;
  const { size } = useAppSelector((state) => state.clientProfile.clientProfileList);
  const isFirstRender = useIsFirstRender();
  const [searchParams] = useSearchParams();
  const noteTypes = searchParams.get('noteTypes');
  const { enableAudioAssist, enableSites } = useFlags();
  const { sites } = useAppSelector((state) => state.clientProfile);
  const isTranscribing =
    note?.aiScribe?.status === 'TRANSCRIBE_STARTED' ||
    note?.aiScribe?.status === 'TRANSCRIBE_IN_PROGRESS' ||
    note?.aiScribe?.status === 'TRANSCRIBE_COMPLETED' ||
    note?.aiScribe?.status === 'SUMMARIZING';
  const memoizedUuid = useMemo(() => uuidv4(), []);

  const sortValues = (val: string[] | string, sortOrder: any[]): string[] => {
    if (Array.isArray(val)) {
      return sortOrder.filter((item) => val.includes(item.id.toString())).map((item) => item.id);
    } else {
      return [val];
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isAutosaving && context === 'popup') {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isAutosaving, context]);

  const debouncedNoteAutoSave = useRef(
    debounce(
      async (formikValues, id, types) => {
        if (formikValues === null) return;
        setIsAutosaving(true);
        if (id) {
          const dataUpdate = {
            clientId: clientId,
            id,
            eventTimestamp: new Date().getTime(),
            uuid: formikValues.uuid,
            note: formikValues.note,
            draft: true,
            noteType: formikValues.noteType || null,
            clientReached: formikValues.clientReached || null,
            dateOfEncounter: isValidDateOrTimeOrNull(formikValues.dateOfEncounter, 'date'),
            startTimeOfEncounter: isValidDateOrTimeOrNull(
              formikValues.startTimeOfEncounter,
              'time'
            ),
            nextContact: formikValues.nextContact || null,
            typeOfSupport: formikValues.typeOfSupport || null,
            timeSpent: formikValues.timeSpent,
            siteId: formikValues.siteId ?? null,
            zCodes: formikValues.zCodes ?? []
          };
          const res = await dispatch(putUpdateNote(dataUpdate));

          if (res.meta.requestStatus === 'fulfilled') {
            setLastSaved(new Date().toISOString());
            const channel = new BroadcastChannel('note-update');
            channel.postMessage({ message: 'EDIT_NOTE', data: dataUpdate });

            if (formikValues.noteType === 'SAFETY_NOTE' || note?.noteType === 'SAFETY_NOTE') {
              channel.postMessage({ message: 'SAFETY_NOTE' });
            }
            channel.close();
          }
        } else {
          const res = await dispatch(
            postCreateNote({
              uuid: formikValues.uuid,
              eventTimestamp: new Date().getTime(),
              clientId,
              organizationId: currentOrganizationID,
              note: formikValues.note,
              draft: true,
              noteType: formikValues.noteType || null,
              clientReached: formikValues.clientReached || null,
              dateOfEncounter: isValidDateOrTimeOrNull(formikValues.dateOfEncounter, 'date'),
              startTimeOfEncounter: isValidDateOrTimeOrNull(
                formikValues.startTimeOfEncounter,
                'time'
              ),
              nextContact: formikValues.nextContact || null,
              typeOfSupport: formikValues.typeOfSupport || null,
              timeSpent: formikValues.timeSpent,
              siteId: formikValues.siteId ?? null,
              zCodes: formikValues.zCodes ?? []
            })
          );

          if (res.meta.requestStatus === 'fulfilled') {
            setCurrentNoteID({ currentNoteID: res.payload?.id, clientId });
            setLastSaved(new Date().toISOString());
            const channel = new BroadcastChannel('note-update');
            if (formikValues.noteType === 'SAFETY_NOTE' || note?.noteType === 'SAFETY_NOTE') {
              channel.postMessage({ message: 'SAFETY_NOTE' });
            }
            channel.close();
            await dispatch(
              getClientNotes({
                clientId,
                page: 0,
                size,
                types,
                firstRender: true
              })
            ).then((result: any) => {
              if (result.payload.data.content.length > 0) {
                //TODO need to optimize
                result.payload.data.content.map((noteItem: ClientProfileContentItem) => {
                  if (noteItem.author.profileImageKey) {
                    dispatch(
                      getUsersImageNotes({
                        profileImageKey: noteItem.author.profileImageKey,
                        userId: clientId,
                        noteId: noteItem.id.toString(),
                        notesList: true
                      })
                    );
                  }
                });
              }
            });
          }
        }
        setIsAutosaving(false);
      },
      context === 'popup' ? 300 : 1000
    )
  ).current;

  const formik = useFormik({
    initialValues: {
      note: note?.note || null,
      draft: note?.draft || null,
      noteType: note?.noteType || null,
      clientReached: note?.clientReached || null,
      dateOfEncounter: note?.dateOfEncounter || null,
      startTimeOfEncounter: note?.startTimeOfEncounter || null,
      nextContact: note?.nextContact || null,
      typeOfSupport: note?.typeOfSupport ? sortValues(note.typeOfSupport, typesOfSupportData) : [],
      id: note?.id || null,
      timeSpent: note?.timeSpent || null,
      siteId: note?.siteId || null,
      uuid: note?.uuid ?? memoizedUuid,
      zCodes:
        note?.selectedZCodes?.map((z) => ({ id: z.id, name: `${z.code} - ${z.description}` })) || []
    },
    validateOnMount: true,
    validationSchema: enableSites ? validationSchemaWithSites : validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (noteId) {
        const dataUpdate = {
          clientId: clientId,
          id: noteId,
          uuid: values.uuid,
          eventTimestamp: new Date().getTime(),
          note: values.note ?? '',
          draft: false,
          noteType: values.noteType ?? '',
          clientReached: values.clientReached ?? '',
          dateOfEncounter: values.dateOfEncounter ?? '',
          startTimeOfEncounter: values.startTimeOfEncounter ?? '',
          nextContact: values.nextContact ?? '',
          typeOfSupport: values.typeOfSupport ?? [],
          timeSpent: values.timeSpent ?? 0,
          siteId: values.siteId ?? null,
          zCodes: values.zCodes ?? []
        };
        const res = await dispatch(putUpdateNote(dataUpdate));

        if (res.meta.requestStatus === 'fulfilled') {
          const channel = new BroadcastChannel('note-update');
          channel.postMessage({ message: 'EDIT_NOTE', data: dataUpdate });
          if (values.noteType === 'SAFETY_NOTE' || note?.noteType === 'SAFETY_NOTE') {
            channel.postMessage({ message: 'SAFETY_NOTE' });
          }
          if (values.nextContact != null && values.nextContact !== '') {
            notifyUserSuccess('Next contact to-do created');
            if (context === 'popup') {
              channel.postMessage({ message: 'NEXT_CONTACT_TODO' });
            } else {
              dispatch(getMyTodos());
              dispatch(getRoadmapTodos({ clientId }));
            }
          }
          channel.close();
          removeCurrentNote();
          setLastSaved(null);
          notifyUserSuccess('Note saved.');
          resetForm({
            values: {
              uuid: uuidv4(),
              note: null,
              draft: null,
              noteType: null,
              clientReached: null,
              dateOfEncounter: null,
              startTimeOfEncounter: null,
              nextContact: null,
              typeOfSupport: [],
              id: null,
              timeSpent: null,
              siteId: null,
              zCodes: []
            }
          });
          dispatch(removeNote());

          if (context === 'popup') {
            await dispatch(
              getClientNote({
                clientId,
                noteId
              })
            );
          }
        }
      }
    }
  });

  useEffect(() => {
    if (isFirstRender) return;
    if (!Boolean(formik.values.note)) return;
    if (formik.isSubmitting) return;

    debouncedNoteAutoSave(formik.values, noteId, noteTypes);
  }, [formik.values]);

  const deleteNote = () => {
    formik.resetForm({
      values: {
        uuid: uuidv4(),
        note: null,
        draft: null,
        noteType: null,
        clientReached: null,
        dateOfEncounter: null,
        startTimeOfEncounter: null,
        nextContact: null,
        typeOfSupport: [],
        id: null,
        timeSpent: null,
        siteId: null,
        zCodes: []
      }
    });
    setLastSaved(null);
    dispatch(removeNote());
    setShowConfirmationModal(false);

    if (noteId) {
      dispatch(
        deleteNoteInput({
          clientId,
          noteId
        })
      ).then(() => {
        removeCurrentNote();
        const channel = new BroadcastChannel('note-update');
        channel.postMessage({ message: 'DELETE_NOTE', noteId });
        if (note?.noteType === 'SAFETY_NOTE') {
          channel.postMessage({ message: 'SAFETY_NOTE' });
        }
        window.close();
      });
    }
  };

  return (
    <Stack width="100%" mt="16px" display="flex" gap="28px" data-test-id="profile-notes-form">
      <form
        onSubmit={formik.handleSubmit}
        id="noteEdit"
        onReset={() => {
          formik.resetForm({
            values: {
              uuid: uuidv4(),
              note: null,
              draft: null,
              noteType: null,
              clientReached: null,
              dateOfEncounter: null,
              startTimeOfEncounter: null,
              nextContact: null,
              typeOfSupport: [],
              id: null,
              timeSpent: null,
              siteId: null,
              zCodes: []
            }
          });
          removeCurrentNote();
          dispatch(removeNote());
          setLastSaved(null);
        }}
      >
        {inputOpen && (
          <>
            <div className={style.inputRow}>
              <div className={style.halfRow}>
                <LabelComponent mandatory text="Note Type" />
                <Dropdown
                  smallDropdown
                  withGradient
                  label="Select"
                  data={noteTypeData}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  name="noteType"
                  id="noteType"
                  value={formik.values.noteType ?? ''}
                  disabled={formDisabled}
                />
                <ErrorContainer
                  visible={formik.touched.noteType && formik.errors.noteType}
                  errors={formik.errors}
                  name="noteType"
                />
              </div>
              <div className={style.halfRow}>
                <LabelComponent mandatory text="Client Reached" />
                <Dropdown
                  smallDropdown
                  withGradient
                  label="Select"
                  data={clientReachedData}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  name="clientReached"
                  id="clientReached"
                  value={formik.values.clientReached ?? ''}
                  disabled={formDisabled}
                />
                <ErrorContainer
                  visible={formik.touched.clientReached && formik.errors.clientReached}
                  errors={formik.errors}
                  name="clientReached"
                />
              </div>
            </div>
            <div className={style.inputRow}>
              <div className={style.halfRow}>
                <LabelComponent mandatory text="Date and time" />
                <DateAndTimePopoverContainer
                  //date part
                  dateName={'dateOfEncounter'}
                  isDateErrorVisible={Boolean(
                    formik.touched.dateOfEncounter && formik.errors.dateOfEncounter
                  )}
                  dateErrors={formik.errors}
                  isDateDisabled={formDisabled}
                  dateHandleChange={(date: string) => {
                    formik.setFieldValue('dateOfEncounter', date).then(() => {
                      formik.setFieldTouched('dateOfEncounter', true);
                    });
                  }}
                  dateHandleBlur={() => {
                    formik.setFieldTouched('startTimeOfEncounter', true);
                    formik.setFieldTouched('dateOfEncounter', true);
                  }}
                  dateValue={formik.values.dateOfEncounter}
                  //time part
                  timeName={'startTimeOfEncounter'}
                  timeHandleChange={(time: string) => {
                    formik.setFieldValue('startTimeOfEncounter', time).then(() => {
                      formik.setFieldTouched('startTimeOfEncounter', true);
                    });
                  }}
                  timeHandleBlur={() => {
                    formik.setFieldTouched('startTimeOfEncounter', true);
                    formik.setFieldTouched('dateOfEncounter', true);
                  }}
                  timeSpentValue={formik.values.startTimeOfEncounter?.toString() ?? ''}
                  isTimeFormDisabled={formDisabled}
                  isTimeSpentErrorVisible={
                    formik.touched.startTimeOfEncounter && formik.errors.startTimeOfEncounter
                  }
                  timeSpentErrors={formik.errors}
                />
                <ErrorContainer
                  visible={
                    (formik.touched.dateOfEncounter && formik.errors.dateOfEncounter) ||
                    (formik.touched.startTimeOfEncounter && formik.errors.startTimeOfEncounter)
                  }
                  staticTextError="Date and Time are required"
                  errors={null}
                  name="static"
                />
              </div>
              <div className={style.halfRow}>
                {!enableSites && (
                  <NextContact
                    formik={{
                      setFieldValue: formik.setFieldValue,
                      setFieldTouched: formik.setFieldTouched,
                      values: {
                        nextContact: formik.values.nextContact ?? '',
                        dateOfEncounter: formik.values.dateOfEncounter ?? ''
                      },
                      touched: {
                        nextContact: formik.touched.nextContact ?? false
                      },
                      errors: {
                        nextContact: formik.errors.nextContact ?? ''
                      }
                    }}
                    disabled={formDisabled}
                  />
                )}
                {enableSites && (
                  <TimeSpent
                    formik={{
                      setFieldValue: formik.setFieldValue,
                      setFieldTouched: formik.setFieldTouched,
                      handleChange: formik.handleChange,
                      handleBlur: formik.handleBlur,
                      values: {
                        timeSpent: formik.values.timeSpent
                      },
                      touched: {
                        timeSpent: formik.touched.timeSpent ?? false
                      },
                      errors: {
                        timeSpent: formik.errors.timeSpent ?? ''
                      }
                    }}
                    disabled={formDisabled}
                  />
                )}
              </div>
            </div>
            <div className={style.inputRow}>
              <div className={style.halfRow}>
                {!enableSites && (
                  <TimeSpent
                    formik={{
                      setFieldValue: formik.setFieldValue,
                      setFieldTouched: formik.setFieldTouched,
                      handleChange: formik.handleChange,
                      handleBlur: formik.handleBlur,
                      values: {
                        timeSpent: formik.values.timeSpent
                      },
                      touched: {
                        timeSpent: formik.touched.timeSpent ?? false
                      },
                      errors: {
                        timeSpent: formik.errors.timeSpent ?? ''
                      }
                    }}
                    disabled={formDisabled}
                  />
                )}
                {enableSites && (
                  <NextContact
                    formik={{
                      setFieldValue: formik.setFieldValue,
                      setFieldTouched: formik.setFieldTouched,
                      values: {
                        nextContact: formik.values.nextContact ?? '',
                        dateOfEncounter: formik.values.dateOfEncounter ?? ''
                      },
                      touched: {
                        nextContact: formik.touched.nextContact ?? false
                      },
                      errors: {
                        nextContact: formik.errors.nextContact ?? ''
                      }
                    }}
                    disabled={formDisabled}
                  />
                )}
              </div>
              <div className={style.halfRow}>
                {!enableSites && (
                  <TypeOfSupport
                    formik={{
                      setFieldValue: formik.setFieldValue,
                      setFieldTouched: formik.setFieldTouched,
                      handleChange: formik.handleChange,
                      handleBlur: formik.handleBlur,
                      values: {
                        typeOfSupport: formik.values.typeOfSupport
                      },
                      touched: {
                        typeOfSupport: formik.touched.typeOfSupport ?? false
                      },
                      errors: {
                        typeOfSupport: formik.errors.typeOfSupport ?? ''
                      }
                    }}
                    disabled={formDisabled}
                    typesOfSupport={typesOfSupportData}
                  />
                )}
                {enableSites && (
                  <>
                    <LabelComponent mandatory text="Site" />
                    <Dropdown
                      value={formik.values.siteId?.toString() ?? ''}
                      smallDropdown
                      withGradient
                      label="Select"
                      data={sites.data.map((site) => ({
                        id: site.id,
                        name: site.name
                      }))}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      name="siteId"
                      disabled={formDisabled}
                    />
                    <ErrorContainer
                      visible={formik.touched.siteId && formik.errors.siteId}
                      errors={formik.errors}
                      name="siteId"
                    />
                  </>
                )}
              </div>
            </div>
            {enableSites && (
              <Stack width="100%" mb="16px">
                <TypeOfSupport
                  formik={{
                    setFieldValue: formik.setFieldValue,
                    setFieldTouched: formik.setFieldTouched,
                    handleChange: formik.handleChange,
                    handleBlur: formik.handleBlur,
                    values: {
                      typeOfSupport: formik.values.typeOfSupport
                    },
                    touched: {
                      typeOfSupport: formik.touched.typeOfSupport ?? false
                    },
                    errors: {
                      typeOfSupport: formik.errors.typeOfSupport ?? ''
                    }
                  }}
                  disabled={formDisabled}
                  typesOfSupport={typesOfSupportData}
                />
              </Stack>
            )}
            <ZCodesField formik={formik} disabled={formDisabled} />
          </>
        )}
        {isTranscribing && (
          <Stack
            width="100%"
            mb="16px"
            className={style.aiScribeInProgress}
            gap="8px"
            display="flex"
            flexDirection="row"
            padding="12px"
            alignItems="center"
            boxSizing="border-box"
            borderRadius="8px"
          >
            <AiScribeIcon />
            <Text weight="medium" size="normal" className={style.aiScribeInProgressText}>
              AI Scribe is processing your note, you’ll be able to edit when it's done. Check back
              soon!
            </Text>
          </Stack>
        )}
        {note?.aiScribe?.status === 'FAILED' && note?.aiScribe?.errorMessage && (
          <Stack
            width="100%"
            mb="16px"
            className={style.aiScribeError}
            gap="8px"
            display="flex"
            flexDirection="row"
            padding="12px"
            alignItems="center"
            boxSizing="border-box"
            borderRadius="8px"
          >
            <span className="material-icons">error_outline</span>
            <Text weight="medium" size="normal" className={style.aiScribeErrorText}>
              {note.aiScribe.errorMessage}
            </Text>
          </Stack>
        )}
        {context === 'popup' &&
          note?.aiScribe?.status === 'FAILED' &&
          !note?.aiScribe?.transcript &&
          note?.aiScribe?.audioUrl && <AudioPlayer note={note} />}
        <div className={style.halfRow}>
          <JoyTextArea
            value={formik.values.note || ''}
            id="note"
            name="note"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            placeholder="Type here"
            rows={10}
            disabled={formDisabled}
          />
          <ErrorContainer
            visible={formik.touched?.note && formik.errors?.note}
            errors={formik.errors}
            name="note"
          />
        </div>
        <div className={style.saveOrDiscard}>
          <div className={style.btnWrap}>
            <Button
              text=""
              type="button"
              disabled={isTranscribing}
              icon={<span className={'material-icons'}>delete</span>}
              handleOnClick={() => setShowConfirmationModal(true)}
              customStyleClass={style.actionButton}
            />
            {context === 'popup' && (
              <Button
                text=""
                type="button"
                customStyleClass={style.actionButton}
                disabled={isTranscribing}
                icon={<span className={'material-icons'}>edit</span>}
                handleOnClick={() => setFormDisabled(false)}
              />
            )}
          </div>
          <div className={style.autosavedBoxWrap}>
            <div className={style.autosavedBox}>
              <Stack>
                {isAutosaving ? <LoadingSpinner size={30} /> : <LastSaved lastSaved={lastSaved} />}
              </Stack>
            </div>
            {enableAudioAssist && false && <AudioAssist />}
            <Button
              customStyleClass={style.submit}
              theme="primary"
              text="Final"
              type="submit"
              disabled={!formik.isValid || formik.isSubmitting || isAutosaving}
            />
          </div>
        </div>
        {context === 'popup' && note?.aiScribe?.transcript && <Transcript note={note} />}
        <ModalPopup
          open={showConfirmationModal}
          handleClose={() => setShowConfirmationModal(false)}
        >
          <ConfirmationModalContent
            confirmButtonText="Yes, delete"
            rejectButtonText="No, return to editing"
            title="Are you sure you want to delete your note?"
            customClassInsideContainer={style.deleteNoteModal}
            text=" "
            handleConfirm={deleteNote}
            handleReject={() => setShowConfirmationModal(false)}
          />
        </ModalPopup>
      </form>
    </Stack>
  );
};

export default NoteEdit;
